// src/components/settings/TaxSettings.js
// TAX CODES SEAM 1 VERIFIER: taxcodes-8d4f1a6e
//
// Settings › Taxes. The company's named taxes: each a name, one or more parts (name + rate),
// and one default for new lines. Spatchy fills in nothing; the user enters every tax.
// Shown only where the roll-out switch is on, to Owner / Admin / Manager / Office Staff
// (SettingsPage canSeeTaxes).
//
// Stored in organizations/{orgId}/settings/taxCodes. firestore.rules let staff READ it and deny
// every client write, so every change goes through saveTaxCodes (functions/utils/taxCodes.js),
// which re-checks everything and copies the default's total into the company's taxRate.
// Every action (save a tax, make default, stop using, use again, undo) saves the whole list.
//
// Until the first save there is no doc: the company's existing tax rate shows as "Tax"
// (carryOverCodes), and the first save stores it.
//
// QuickBooks-connected companies see their QuickBooks tax codes read-only (the existing
// getQuickBooksMappingOptions, through qbMappingCache) and cannot add or edit here.
import React, { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Percent, Plus, Pencil, Trash2, Star, PauseCircle, PlayCircle, AlertTriangle, Info, Lock } from 'lucide-react';
import { db, functions } from '../../firebase/config';
import { getQuickBooksMappingOptionsCached } from '../../utils/qbMappingCache';
import {
    TAX_LIMITS, CARRY_LABEL, carryOverCodes, startingTaxCodes, codeTotal, formatRate, describeCode, parseRateInput,
    findTaxCodesProblem, toSavePayload, isStaleTaxCodesError,
} from '../../utils/taxCodes';

const BTN = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
const BTN_GHOST = `${BTN} border-gray-200 text-gray-700 bg-white hover:bg-gray-50`;
const BTN_PRIMARY = `${BTN} border-[#5F4EC9] text-white bg-[#5F4EC9] hover:bg-[#4f40b0]`;
const INPUT = 'w-full p-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#5F4EC9]/30';

const saveTaxCodesCall = (payload) => httpsCallable(functions, 'saveTaxCodes')(payload);

const newPartDraft = () => ({ key: Math.random().toString(36).slice(2), id: null, name: '', rateText: '' });
const draftFromCode = (code) => ({
    id: code ? code.id : null,
    name: code ? code.name : '',
    isDefault: code ? code.isDefault === true : false,
    parts: code
        ? code.parts.map((p) => ({ key: p.id, id: p.id, name: p.name, rateText: formatRate(p.rate) }))
        : [newPartDraft()],
});
const codeFromDraft = (draft, stored) => ({
    id: draft.id || undefined,
    name: draft.name.trim(),
    parts: draft.parts.map((p) => ({ id: p.id || undefined, name: p.name.trim(), rate: parseRateInput(p.rateText) })),
    isDefault: draft.isDefault === true,
    stopped: stored ? stored.stopped === true : false,
});

export default function TaxSettings({ currentUserProfile, companySettings }) {
    const orgId = currentUserProfile?.organizationId;
    const qbConnected = companySettings?.quickbooksConnected === true;

    const [stored, setStored] = useState(undefined); // undefined = loading, null = no doc yet
    const [loadError, setLoadError] = useState('');
    const [editor, setEditor] = useState(null);      // null | { draft, error }
    const [saving, setSaving] = useState(false);
    const [pageError, setPageError] = useState('');
    const [notice, setNotice] = useState(null);      // { text, undoId? }

    useEffect(() => {
        if (!orgId || qbConnected) return undefined;
        setStored(undefined);
        return onSnapshot(doc(db, `organizations/${orgId}/settings/taxCodes`), (snap) => {
            setStored(snap.exists() ? snap.data() : null);
            setLoadError('');
        }, (err) => {
            console.error('TaxSettings: could not load taxes', err);
            setLoadError('Could not load your taxes. Please refresh the page.');
        });
    }, [orgId, qbConnected]);

    const carriedOver = stored !== undefined && !(stored && Array.isArray(stored.codes)); // taxlines-771ce983 — no codes saved yet
    const codes = useMemo(() => {
        if (stored === undefined || !companySettings) return null;
        return startingTaxCodes(stored, companySettings); // taxlines-771ce983 — a doc holding only the switch still starts from the carry-over
    }, [stored, companySettings]);
    const version = stored ? (stored.version || 0) : 0;

    // Saves the whole list. Returns true when saved.
    const save = async (nextCodes, { onError, successNotice } = {}) => {
        const problem = findTaxCodesProblem(nextCodes, codes);
        if (problem) { (onError || setPageError)(problem.message); return false; }
        setSaving(true);
        setPageError('');
        try {
            await saveTaxCodesCall({ codes: toSavePayload(nextCodes), expectedVersion: version });
            setNotice(successNotice || null);
            return true;
        } catch (err) {
            console.error('TaxSettings: save failed', err);
            const msg = isStaleTaxCodesError(err)
                ? 'Someone else changed your taxes. The page now shows their changes — please check and try again.'
                : (err?.message || 'Could not save your taxes. Please try again.');
            (onError || setPageError)(msg);
            return false;
        } finally {
            setSaving(false);
        }
    };

    const withDefault = (list, id) => list.map((c) => ({ ...c, isDefault: c.id === id }));

    const saveEditor = async () => {
        const { draft } = editor;
        for (const p of draft.parts) {
            if (String(p.rateText).trim() !== '' && parseRateInput(p.rateText) === undefined) {
                setEditor((e) => ({ ...e, error: `The rate for "${p.name.trim() || 'a part'}" must be a number, like 5 or 7.125.` }));
                return;
            }
        }
        const original = draft.id ? codes.find((c) => c.id === draft.id) : null;
        const edited = codeFromDraft(draft, original);
        let next = draft.id ? codes.map((c) => (c.id === draft.id ? { ...c, ...edited } : c)) : [...codes, edited];
        if (edited.isDefault) next = next.map((c) => ((draft.id ? c.id === draft.id : c === edited) ? c : { ...c, isDefault: false }));
        const ok = await save(next, {
            onError: (message) => setEditor((e) => (e ? { ...e, error: message } : e)),
            successNotice: { text: `"${edited.name}" saved.` },
        });
        if (ok) setEditor(null);
    };

    const makeDefault = (code) => save(withDefault(codes, code.id), { successNotice: { text: `"${code.name}" is now used on new lines by default.` } });
    const setStopped = (code, stopped, notice) =>
        save(codes.map((c) => (c.id === code.id ? { ...c, stopped } : c)), { successNotice: notice });
    const stopUsing = (code) => setStopped(code, true, { text: `"${code.name}" moved to No longer used.`, undoId: code.id });
    const restoreCode = (code) => setStopped(code, false, { text: `"${code.name}" is in use again.` });
    const undoStop = () => {
        const code = codes.find((c) => c.id === notice?.undoId);
        if (code) setStopped(code, false, { text: `"${code.name}" is in use again.` });
    };

    if (!orgId) return null;

    return (
        <div className="p-6 sm:p-8 space-y-6">
            <div>
                <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2"><Percent size={20} className="text-[#5F4EC9]" /> Taxes</h2>
                <p className="text-sm text-gray-500 mt-1">The taxes you charge on estimates, jobs and invoices.</p>
            </div>

            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900">
                <Info size={16} className="mt-0.5 shrink-0" />
                <span>Spatchy calculates exactly what you enter here. Please check the names and rates with your accountant.</span>
            </div>

            {qbConnected ? <QuickBooksTaxes orgId={orgId} /> : (
                <>
                    {loadError && <ErrorLine text={loadError} />}
                    {!loadError && codes === null && <p className="text-sm text-gray-500">Loading your taxes…</p>}
                    {!loadError && codes !== null && (
                        <>
                            {pageError && <ErrorLine text={pageError} />}
                            {notice && (
                                <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-900">
                                    <span>{notice.text}</span>
                                    <span className="flex items-center gap-3">
                                        {notice.undoId && <button type="button" className="font-semibold underline disabled:opacity-50" disabled={saving} onClick={undoStop}>Undo</button>}
                                        <button type="button" className="text-green-700" aria-label="Dismiss" onClick={() => setNotice(null)}>×</button>
                                    </span>
                                </div>
                            )}

                            <section className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">In use</h3>
                                    {!editor && codes.length < TAX_LIMITS.codes && (
                                        <button type="button" className={BTN_PRIMARY} disabled={saving} onClick={() => { setNotice(null); setPageError(''); setEditor({ draft: draftFromCode(null), error: '' }); }}>
                                            <Plus size={16} /> Add a tax
                                        </button>
                                    )}
                                </div>
                                {editor && !editor.draft.id && (
                                    <TaxEditor editor={editor} setEditor={setEditor} saving={saving} onSave={saveEditor} isOnlyDefault={false} />
                                )}
                                {codes.filter((c) => !c.stopped).map((code) => (
                                    editor && editor.draft.id === code.id ? (
                                        <TaxEditor key={code.id} editor={editor} setEditor={setEditor} saving={saving} onSave={saveEditor} isOnlyDefault={code.isDefault} />
                                    ) : (
                                        <TaxCard key={code.id} code={code} carriedOver={carriedOver && code.carriedOver} busy={saving || !!editor}
                                            onEdit={() => { setNotice(null); setPageError(''); setEditor({ draft: draftFromCode(code), error: '' }); }}
                                            onMakeDefault={() => makeDefault(code)}
                                            onStop={() => stopUsing(code)} />
                                    )
                                ))}
                            </section>

                            {codes.some((c) => c.stopped) && (
                                <section className="space-y-3">
                                    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">No longer used</h3>
                                    <p className="text-xs text-gray-500">These aren't offered on new lines. Estimates and invoices that already use them keep them.</p>
                                    {codes.filter((c) => c.stopped).map((code) => (
                                        <div key={code.id} className="flex flex-wrap items-center justify-between gap-3 p-4 rounded-xl border border-gray-200 bg-gray-50">
                                            <div>
                                                <p className="font-medium text-gray-600">{code.name}</p>
                                                <p className="text-sm text-gray-500">{describeCode(code)}</p>
                                            </div>
                                            <button type="button" className={BTN_GHOST} disabled={saving || !!editor} onClick={() => restoreCode(code)}>
                                                <PlayCircle size={16} /> Use again
                                            </button>
                                        </div>
                                    ))}
                                </section>
                            )}

                            <p className="text-xs text-gray-500">Changing a rate affects new lines only. Estimates and invoices you've already made keep the tax they were made with.</p>
                        </>
                    )}
                </>
            )}
        </div>
    );
}

function ErrorLine({ text }) {
    return (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" /><span>{text}</span>
        </div>
    );
}

function TaxCard({ code, carriedOver, busy, onEdit, onMakeDefault, onStop }) {
    return (
        <div className="p-4 rounded-xl border border-gray-200 bg-white">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <p className="font-semibold text-gray-800 flex items-center gap-2">
                        {code.name}
                        {code.isDefault && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-[#5F4EC9]/10 text-[#5F4EC9]">
                                <Star size={12} /> Default for new lines
                            </span>
                        )}
                    </p>
                    <p className="text-sm text-gray-600 mt-0.5">{describeCode(code)}</p>
                    {carriedOver && <p className="text-xs text-gray-500 mt-1 italic">{CARRY_LABEL}</p>}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <button type="button" className={BTN_GHOST} disabled={busy} onClick={onEdit}><Pencil size={14} /> Edit</button>
                    {!code.isDefault && (
                        <button type="button" className={BTN_GHOST} disabled={busy} onClick={onMakeDefault}><Star size={14} /> Make default</button>
                    )}
                    <button type="button" className={BTN_GHOST} disabled={busy || code.isDefault} onClick={onStop}
                        title={code.isDefault ? "The default can't be stopped. Make another tax the default first." : undefined}>
                        <PauseCircle size={14} /> Stop using
                    </button>
                </div>
            </div>
            {code.isDefault && <p className="text-xs text-gray-400 mt-2">The default can't be stopped. Make another tax the default first.</p>}
        </div>
    );
}

function TaxEditor({ editor, setEditor, saving, onSave, isOnlyDefault }) {
    const { draft, error } = editor;
    const set = (patch) => setEditor((e) => ({ ...e, error: '', draft: { ...e.draft, ...patch } }));
    const setPart = (key, patch) => set({ parts: draft.parts.map((p) => (p.key === key ? { ...p, ...patch } : p)) });
    const rates = draft.parts.map((p) => parseRateInput(p.rateText));
    const total = rates.every((r) => r !== undefined) ? formatRate(codeTotal({ parts: rates.map((rate) => ({ rate })) })) : null;

    return (
        <div className="p-4 rounded-xl border-2 border-[#5F4EC9]/40 bg-[#5F4EC9]/[0.03] space-y-4">
            <div>
                <label className="block text-sm font-medium text-gray-700">Name <span className="text-gray-400 font-normal">(shown to customers)</span></label>
                <input className={`${INPUT} mt-1`} value={draft.name} maxLength={TAX_LIMITS.name} placeholder="Name this tax"
                    onChange={(e) => set({ name: e.target.value })} />
            </div>
            <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">Parts</p>
                {draft.parts.map((p) => (
                    <div key={p.key} className="flex items-center gap-2">
                        <input className={`${INPUT} flex-grow`} value={p.name} maxLength={TAX_LIMITS.name} placeholder="Part name"
                            aria-label="Part name" onChange={(e) => setPart(p.key, { name: e.target.value })} />
                        <div className="relative w-28 shrink-0">
                            <input className={`${INPUT} pr-7 text-right`} value={p.rateText} inputMode="decimal" placeholder="Rate"
                                aria-label="Rate in percent" onChange={(e) => setPart(p.key, { rateText: e.target.value })} />
                            <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-sm text-gray-400">%</span>
                        </div>
                        <button type="button" className="p-2 text-gray-400 hover:text-red-600 disabled:opacity-30" aria-label="Remove this part"
                            disabled={draft.parts.length <= 1} onClick={() => set({ parts: draft.parts.filter((x) => x.key !== p.key) })}>
                            <Trash2 size={16} />
                        </button>
                    </div>
                ))}
                {draft.parts.length < TAX_LIMITS.parts && (
                    <button type="button" className="text-sm font-medium text-[#5F4EC9] hover:underline inline-flex items-center gap-1"
                        onClick={() => set({ parts: [...draft.parts, newPartDraft()] })}>
                        <Plus size={14} /> Add a part
                    </button>
                )}
                <p className="text-sm text-gray-600">Total: <span className="font-semibold">{total === null ? '—' : `${total}%`}</span></p>
            </div>
            <label className={`flex items-center gap-2 text-sm text-gray-700 ${isOnlyDefault ? 'opacity-60' : ''}`}>
                <input type="checkbox" checked={draft.isDefault} disabled={isOnlyDefault}
                    onChange={(e) => set({ isDefault: e.target.checked })} />
                Use on new lines by default
                {isOnlyDefault && <span className="text-xs text-gray-400 inline-flex items-center gap-1"><Lock size={12} /> make another tax the default to change this</span>}
            </label>
            {error && <ErrorLine text={error} />}
            <div className="flex items-center gap-2">
                <button type="button" className={BTN_PRIMARY} disabled={saving} onClick={onSave}>{saving ? 'Saving…' : 'Save'}</button>
                <button type="button" className={BTN_GHOST} disabled={saving} onClick={() => setEditor(null)}>Cancel</button>
            </div>
        </div>
    );
}

function QuickBooksTaxes({ orgId }) {
    const [state, setState] = useState({ loading: true, codes: [], error: '' });
    useEffect(() => {
        let alive = true;
        setState({ loading: true, codes: [], error: '' });
        getQuickBooksMappingOptionsCached(orgId)
            .then((data) => { if (alive) setState({ loading: false, codes: (data && data.taxCodes) || [], error: '' }); })
            .catch((err) => {
                console.error('TaxSettings: could not load QuickBooks taxes', err);
                if (alive) setState({ loading: false, codes: [], error: 'Could not load your QuickBooks taxes. Please try again later.' });
            });
        return () => { alive = false; };
    }, [orgId]);

    return (
        <section className="space-y-3">
            <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-900">
                <Lock size={16} className="mt-0.5 shrink-0" />
                <span><span className="font-semibold">Your taxes come from QuickBooks.</span> To add or change a tax, do it in QuickBooks.</span>
            </div>
            {state.loading && <p className="text-sm text-gray-500">Loading your QuickBooks taxes…</p>}
            {state.error && <ErrorLine text={state.error} />}
            {!state.loading && !state.error && state.codes.length === 0 && <p className="text-sm text-gray-500">No taxes found in QuickBooks.</p>}
            {state.codes.map((c) => (
                <div key={c.Id} className="flex items-center justify-between p-4 rounded-xl border border-gray-200 bg-white">
                    <p className="font-medium text-gray-800">{c.Name}</p>
                    <p className="text-sm text-gray-600">{formatRate(Number(c.Rate) || 0)}%</p>
                </div>
            ))}
        </section>
    );
}
